/**
 * Derived views of the replica.
 *
 * Each hook recomputes only when the replica version changes, so a roll-call screen that
 * touches one mark re-derives its own lists without any query round-trip.
 */

import { useMemo } from 'react';

import {
  MemberRole,
  type IsoDate,
  type Membership,
  type Person,
  type PersonId,
  type Project,
  type ProjectId,
} from '../../shared/types.js';
import { useReplicaVersion, useStore } from './context.js';

/** Compares people by display name, case-insensitively and locale-aware. */
function byName(left: Person, right: Person): number {
  return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
}

/** Lists the live projects the user can see, most recently updated first. */
export function useProjects(): Project[] {
  const store = useStore();
  const version = useReplicaVersion();
  return useMemo(
    () => [...store.replica.projects.values()]
      .filter((project) => project.deletedAt === null)
      .sort((left, right) => right.updatedAt - left.updatedAt),
    [store, version],
  );
}

/** Reads one project, or `undefined` when it is unknown or deleted. */
export function useProject(projectId: ProjectId | undefined): Project | undefined {
  const store = useStore();
  const version = useReplicaVersion();
  return useMemo(() => {
    if (projectId === undefined) return undefined;
    const project = store.replica.projects.get(projectId);
    return project === undefined || project.deletedAt !== null ? undefined : project;
  }, [store, version, projectId]);
}

/** Lists a project's live roster in name order. */
export function usePeople(projectId: ProjectId | undefined): Person[] {
  const store = useStore();
  const version = useReplicaVersion();
  return useMemo(() => {
    if (projectId === undefined) return [];
    return [...store.replica.people.values()]
      .filter((person) => person.projectId === projectId && person.deletedAt === null)
      .sort(byName);
  }, [store, version, projectId]);
}

/** Reads one person, or `undefined` when unknown. Deleted people stay readable for history. */
export function usePerson(personId: PersonId | undefined): Person | undefined {
  const store = useStore();
  const version = useReplicaVersion();
  return useMemo(
    () => (personId === undefined ? undefined : store.replica.people.get(personId)),
    [store, version, personId],
  );
}

/** Lists a project's live memberships, owners first and then by email. */
export function useMemberships(projectId: ProjectId | undefined): Membership[] {
  const store = useStore();
  const version = useReplicaVersion();
  return useMemo(() => {
    if (projectId === undefined) return [];
    return [...store.replica.memberships.values()]
      .filter((membership) => membership.projectId === projectId && membership.deletedAt === null)
      .sort((left, right) => {
        if (left.role !== right.role) return left.role === MemberRole.Owner ? -1 : 1;
        return left.email.localeCompare(right.email);
      });
  }, [store, version, projectId]);
}

/** Returns the signed-in user's role on a project, or `undefined` when they are not a member. */
export function useOwnRole(projectId: ProjectId | undefined): MemberRole | undefined {
  const store = useStore();
  const version = useReplicaVersion();
  return useMemo(() => {
    const email = store.identity?.email;
    if (projectId === undefined || email === undefined) return undefined;
    for (const membership of store.replica.memberships.values()) {
      if (membership.projectId !== projectId || membership.deletedAt !== null) continue;
      if (membership.email === email) return membership.role;
    }
    return undefined;
  }, [store, version, projectId]);
}

/** What another account has changed in one session recently. */
export interface RemoteEditSummary {
  /** Number of marks in the session last written by somebody else. */
  count: number;
  /** Email of the most recent other author, or `null` when there is none. */
  author: string | null;
  /** When that most recent change was written. */
  at: number;
}

/** How far back a change by somebody else is still worth pointing out. */
const REMOTE_EDIT_WINDOW_MS = 15 * 60 * 1000;

/**
 * Summarizes recent changes to one session made by another account.
 *
 * Two people taking the same roll call is the case this exists for: without it, one person's
 * tap replaces the other's with nothing on screen to say so. Marks with no recorded author
 * predate authorship and are ignored rather than blamed on anyone.
 */
export function useRemoteEdits(projectId: ProjectId | undefined, date: IsoDate | null): RemoteEditSummary {
  const store = useStore();
  const version = useReplicaVersion();
  return useMemo(() => {
    const me = store.identity?.email ?? '';
    const summary: RemoteEditSummary = { count: 0, author: null, at: 0 };
    if (projectId === undefined || date === null) return summary;
    const horizon = Date.now() - REMOTE_EDIT_WINDOW_MS;
    for (const mark of store.replica.attendanceMarks.values()) {
      if (mark.projectId !== projectId || mark.date !== date) continue;
      if (mark.updatedBy === '' || mark.updatedBy === me) continue;
      if (mark.updatedAt < horizon) continue;
      summary.count += 1;
      if (mark.updatedAt > summary.at) {
        summary.at = mark.updatedAt;
        summary.author = mark.updatedBy;
      }
    }
    return summary;
  }, [store, version, projectId, date]);
}
